import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, Sphere, MeshDistortMaterial, Float, Torus, Sparkles } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import SEO from './SEO';
import './DnyanaaiAstrology.css';

const CelestialSphere = () => {
  const sphereRef = useRef();
  useFrame((state, delta) => {
    if (sphereRef.current) {
      sphereRef.current.rotation.y += delta * 0.15;
      sphereRef.current.rotation.x += delta * 0.05;
    }
  });

  return (
    <Sphere ref={sphereRef} args={[2.5, 64, 64]} position={[2, 0, -4]}>
      <MeshDistortMaterial
        color="#8a2be2"
        attach="material"
        distort={0.3}
        speed={1.5}
        roughness={0.2}
        metalness={0.8}
        emissive="#2d0a30"
        emissiveIntensity={1}
      />
    </Sphere>
  );
};

const MysticalRings = () => {
  return (
    <>
      <Float speed={2} rotationIntensity={1.5} floatIntensity={2}>
        <Torus args={[3.2, 0.02, 16, 100]} position={[2, 0, -4]} rotation={[Math.PI / 3, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#d4af37" emissive="#d4af37" emissiveIntensity={2} roughness={0.1} metalness={1} />
        </Torus>
      </Float>
      <Float speed={1.5} rotationIntensity={2} floatIntensity={1}>
        <Torus args={[3.8, 0.01, 16, 100]} position={[2, 0, -4]} rotation={[Math.PI / 6, Math.PI / 2, 0]}>
          <meshStandardMaterial color="#8a2be2" emissive="#8a2be2" emissiveIntensity={1} roughness={0.1} metalness={1} />
        </Torus>
      </Float>
    </>
  );
};

const CameraRig = () => {
  useFrame((state) => {
    state.camera.position.lerp(
      new THREE.Vector3(state.pointer.x * 0.8, state.pointer.y * 0.8, 5),
      0.05
    );
    state.camera.lookAt(0, 0, 0);
  });
  return null;
};

const Astrology3DEnvironment = () => {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[-10, -10, -10]} color="#d4af37" intensity={2} />
      
      {/* Mystical dust */}
      <Sparkles count={300} scale={12} size={4} speed={0.4} opacity={0.3} color="#d4af37" />
      <Sparkles count={200} scale={10} size={3} speed={0.2} opacity={0.5} color="#8a2be2" />
      
      <Stars radius={50} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
      
      <CelestialSphere />
      <MysticalRings />
      
      <CameraRig />

      <EffectComposer disableNormalPass>
        <Bloom luminanceThreshold={0.1} mipmapBlur intensity={1.5} />
        <Vignette eskil={false} offset={0.1} darkness={1.1} />
      </EffectComposer>
    </>
  );
};

function DnyanaaiAstrology() {
  const [formData, setFormData] = useState({
    name: '', phone: '', dob: '', birthTime: '', birthPlace: '', query: ''
  });

  const astrologyStructuredData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Dnyanaai Astrology",
    "description": "Expert Vedic astrology services including Kundali analysis, marriage matching, career guidance, and Vastu consultation.",
    "url": "https://bramhaware.in/astrology",
    "serviceType": ["Kundali Analysis", "Marriage Matching", "Career Guidance", "Vastu Consultation", "Numerology"],
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "availableLanguage": ["en", "mr", "hi"],
    "priceRange": "₹₹",
    "provider": {
      "@type": "Organization",
      "name": "BRAMHAWARE"
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const { name, phone, dob, birthTime, birthPlace, query } = formData;
    const message = `🙏 नमस्कार! Consultation Request\n\nName: ${name}\nPhone: ${phone}\nDate of Birth: ${dob}\nBirth Time: ${birthTime}\nBirth Place: ${birthPlace}\n\nQuery: ${query}`;
    window.open(`https://wa.me/91XXXXXXXXXX?text=${encodeURIComponent(message)}`, '_blank');
  };

  const zodiacSigns = [
    { sign: '♈', name: 'Aries', nameMr: 'मेष', element: 'Fire' },
    { sign: '♉', name: 'Taurus', nameMr: 'वृषभ', element: 'Earth' },
    { sign: '♊', name: 'Gemini', nameMr: 'मिथुन', element: 'Air' },
    { sign: '♋', name: 'Cancer', nameMr: 'कर्क', element: 'Water' },
    { sign: '♌', name: 'Leo', nameMr: 'सिंह', element: 'Fire' },
    { sign: '♍', name: 'Virgo', nameMr: 'कन्या', element: 'Earth' },
    { sign: '♎', name: 'Libra', nameMr: 'तुला', element: 'Air' },
    { sign: '♏', name: 'Scorpio', nameMr: 'वृश्चिक', element: 'Water' },
    { sign: '♐', name: 'Sagittarius', nameMr: 'धनु', element: 'Fire' },
    { sign: '♑', name: 'Capricorn', nameMr: 'मकर', element: 'Earth' },
    { sign: '♒', name: 'Aquarius', nameMr: 'कुंभ', element: 'Air' },
    { sign: '♓', name: 'Pisces', nameMr: 'मीन', element: 'Water' }
  ];

  const testimonials = [
    { name: 'श्री. राजेश पाटील', location: 'पुणे', text: 'अत्यंत अचूक भविष्यकथन! विवाह जुळणीसाठी मोलाचा सल्ला मिळाला.', rating: 5 },
    { name: 'सौ. प्रिया देशमुख', location: 'मुंबई', text: 'करिअर मार्गदर्शन खूप उपयुक्त ठरले. धन्यवाद!', rating: 5 },
    { name: 'श्री. अमित जोशी', location: 'नाशिक', text: 'वास्तु शास्त्राच्या सल्ल्यामुळे घरात सकारात्मकता आली.', rating: 5 },
    { name: 'सौ. स्नेहा कुलकर्णी', location: 'नागपूर', text: 'कुंडली विश्लेषण अगदी सविस्तर आणि समजण्यास सोपे.', rating: 5 }
  ];

  const services = [
    { icon: '🪐', title: 'Kundali Analysis', titleMr: 'कुंडली विश्लेषण', desc: 'Detailed birth chart reading and life predictions' },
    { icon: '💑', title: 'Marriage Matching', titleMr: 'विवाह जुळणी', desc: 'Kundali milan for compatibility analysis' },
    { icon: '💼', title: 'Career Guidance', titleMr: 'करिअर मार्गदर्शन', desc: 'Professional path based on planetary positions' },
    { icon: '🏠', title: 'Vastu Shastra', titleMr: 'वास्तु शास्त्र', desc: 'Home and office energy alignment' },
    { icon: '💎', title: 'Gemstone Advice', titleMr: 'रत्न सल्ला', desc: 'Personalized gemstone recommendations' },
    { icon: '🕉️', title: 'Remedial Solutions', titleMr: 'उपाय योजना', desc: 'Puja, mantra and spiritual remedies' }
  ];

  const features = [
    { icon: '📜', text: 'Vedic Astrology Expertise' },
    { icon: '🎓', text: '15+ Years Experience' },
    { icon: '🌍', text: 'Online & In-Person Consultations' },
    { icon: '🔮', text: 'Accurate Predictions' },
    { icon: '📞', text: 'Personal Guidance' },
    { icon: '⭐', text: '1000+ Satisfied Clients' }
  ];

  return (
    <div className="astrology-page">
      <div className="canvas-container">
        <Canvas camera={{ position: [0, 0, 5], fov: 60 }}>
          <Astrology3DEnvironment />
        </Canvas>
      </div>
      <div className="astrology-content-wrapper">
        <SEO
        title="Dnyanaai Astrology - ज्योतिष मार्गदर्शन | Vedic Astrology Services"
        description="Expert Vedic astrology services - Kundali analysis, marriage matching (Kundali Milan), career guidance, Vastu consultation, and numerology. Trusted astrologers in Pune, Maharashtra."
        keywords="Vedic Astrology, Kundali, Horoscope, Jyotish, Marriage Matching, Kundali Milan, Career Astrology, Vastu Shastra, Numerology, Pune Astrologer, Maharashtra, ज्योतिष"
        canonicalUrl="/astrology"
        ogImage="/dnynaailogo.png"
        structuredData={astrologyStructuredData}
      />
      {/* Header */}
      <header className="astrology-header">
        <div className="container">
          <Link to="/" className="back-link">← Back to BRAMHAWARE</Link>
          <div className="header-content">
            <img src="/dnynaailogo.png" alt="Dnyanaai Astrology" className="header-logo" />
            <div className="header-text">
              <h1>Dnyanaai Astrology</h1>
              <p className="tagline">ज्ञानाई ज्योतिष | Divine Guidance Through Stars</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="astrology-hero">
        <div className="cosmic-bg"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <span className="hero-eyebrow">✦ Vedic Astrology ✦</span>
              <h2>Unlock Your <span>Destiny</span></h2>
              <p className="hero-subtitle">आपल्या भविष्याचे रहस्य जाणून घ्या</p>
              <p className="hero-desc">
                Discover the cosmic blueprint of your life through ancient Vedic wisdom. 
                Get personalized guidance for love, career, health, and spiritual growth.
              </p>
              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-number">1000+</span>
                  <span className="hero-stat-label">Clients</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-number">15+</span>
                  <span className="hero-stat-label">Years</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-number">6</span>
                  <span className="hero-stat-label">Services</span>
                </div>
              </div>
              <div className="hero-buttons">
                <a href="#booking" className="btn btn-primary">📅 Book Consultation</a>
                <a href="#services" className="btn btn-secondary">Explore Services</a>
              </div>
            </div>
            <div className="hero-image">
              <div className="hero-image-glow"></div>
              <img src="/dnynaailogowithbackground.jpg" alt="Dnyanaai" className="astrologer-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <h2 className="section-title">Our Services | आमच्या सेवा</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Comprehensive astrological guidance for all aspects of life</p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <span className="service-icon">{service.icon}</span>
                <h3>{service.title}</h3>
                <p className="service-title-mr">{service.titleMr}</p>
                <p className="service-desc">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us-section">
        <div className="container">
          <h2 className="section-title">Why Choose Dnyanaai?</h2>
          <div className="section-divider"></div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <span className="feature-icon">{feature.icon}</span>
                <span className="feature-text">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zodiac Signs Section */}
      <section className="zodiac-section">
        <div className="container">
          <h2 className="section-title">राशी भविष्य | Zodiac Signs</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">आपली राशी निवडा आणि मार्गदर्शन मिळवा</p>
          <div className="zodiac-grid">
            {zodiacSigns.map((zodiac, index) => (
              <div key={index} className={`zodiac-card element-${zodiac.element.toLowerCase()}`}>
                <span className="zodiac-symbol">{zodiac.sign}</span>
                <h4>{zodiac.name}</h4>
                <p className="zodiac-mr">{zodiac.nameMr}</p>
                <span className="element-tag">{zodiac.element}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">ग्राहक अभिप्राय | Testimonials</h2>
          <div className="section-divider"></div>
          <div className="testimonials-grid">
            {testimonials.map((t, index) => (
              <div key={index} className="testimonial-card">
                <div className="stars">{'⭐'.repeat(t.rating)}</div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <strong>{t.name}</strong>
                  <span>{t.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking" className="booking-section">
        <div className="container">
          <h2 className="section-title">Book Consultation | सल्ला बुक करा</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">आपली जन्म माहिती भरा आणि आम्ही संपर्क करू</p>
          <form className="booking-form" onSubmit={handleFormSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>नाव / Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleFormChange} required placeholder="Your full name" />
              </div>
              <div className="form-group">
                <label>फोन / Phone *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required placeholder="+91 XXXXX XXXXX" />
              </div>
              <div className="form-group">
                <label>जन्म तारीख / Date of Birth *</label>
                <input type="date" name="dob" value={formData.dob} onChange={handleFormChange} required />
              </div>
              <div className="form-group">
                <label>जन्म वेळ / Birth Time</label>
                <input type="time" name="birthTime" value={formData.birthTime} onChange={handleFormChange} />
              </div>
              <div className="form-group full-width">
                <label>जन्म स्थान / Birth Place *</label>
                <input type="text" name="birthPlace" value={formData.birthPlace} onChange={handleFormChange} required placeholder="City, State" />
              </div>
              <div className="form-group full-width">
                <label>आपला प्रश्न / Your Query</label>
                <textarea name="query" value={formData.query} onChange={handleFormChange} rows="3" placeholder="Marriage, Career, Health..."></textarea>
              </div>
            </div>
            <button type="submit" className="btn btn-primary submit-btn">
              📱 Send via WhatsApp
            </button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <h2 className="section-title">Get Your Reading | संपर्क करा</h2>
          <div className="section-divider"></div>
          <div className="contact-content">
            <div className="contact-info-panel">
              <h3 className="contact-panel-title">📡 Contact Details</h3>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <div>
                  <h4>Phone / WhatsApp</h4>
                  <p>+91 XXXXX XXXXX</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <div>
                  <h4>Email</h4>
                  <p>dnyanaai@bramhaware.in</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <div>
                  <h4>Location</h4>
                  <p>Maharashtra, India</p>
                </div>
              </div>
            </div>
            <div className="consultation-panel">
              <h3 className="contact-panel-title">🔮 Consultation Modes</h3>
              <div className="consultation-types-grid">
                <div className="type-card">
                  <span>🎥</span>
                  <span>Video Call — Online anywhere in India</span>
                </div>
                <div className="type-card">
                  <span>📞</span>
                  <span>Phone Call — Quick guidance, anytime</span>
                </div>
                <div className="type-card">
                  <span>🏠</span>
                  <span>In-Person — Visit us in Maharashtra</span>
                </div>
                <div className="type-card">
                  <span>💬</span>
                  <span>WhatsApp — Chat consultation available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a href="https://wa.me/918177887927?text=🙏%20नमस्कार!%20I%20want%20to%20book%20an%20astrology%20consultation."
         className="whatsapp-float" target="_blank" rel="noopener noreferrer" title="Chat on WhatsApp">
        <span className="whatsapp-icon">💬</span>
        <span className="whatsapp-text">WhatsApp</span>
      </a>

      {/* Footer */}
      <footer className="astrology-footer">
        <div className="container">
          <div className="footer-quote">
            <p className="shloka">॥ ज्ञानं परमं बलम् ॥</p>
            <p className="shloka-meaning">Knowledge is the Supreme Power</p>
          </div>
          <p>© {new Date().getFullYear()} Dnyanaai Astrology | A BRAMHAWARE Partner</p>
          <Link to="/">www.bramhaware.in</Link>
        </div>
      </footer>
      </div>
    </div>
  );
}

export default DnyanaaiAstrology;

